import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { gql, useApolloClient } from '@apollo/client';
import { useRootStore } from '@/lib/providers/RootProvider';
import ZoneCount from './ZoneCount';
import type {
  ZoneCountProduct,
  ZoneCountSheet,
  ZoneCountUnitLookup
} from './zoneCountSteps';

// Plain strings rather than fragments so scripts/gql-frontend-validate.cjs resolves them against the
// schema too.
const ZONE_FIELDS = `
  id
  name
  zoneType
  locationId
`;

const PRODUCT_INFO_FIELDS = `
  key
  fdaProductId
  upcProductId
  productName
  ndc
  tenDigitNdc
  upc
  deaSchedule
  kind
  controlled
  matchKeys
`;

export const ZONE_COUNT_SHEET = gql`
  query zoneCountSheet($companyId: ID!, $zoneId: ID!) {
    zoneCountSheet(companyId: $companyId, zoneId: $zoneId) {
      zone {
        ${ZONE_FIELDS}
      }
      locationId
      expectedTotal
      notes
      readAt
      products {
        ${PRODUCT_INFO_FIELDS}
        expected
        lots {
          key
          zoneInventoryBalanceId
          lot
          expiration
          expected
          bulkQuantity
          units {
            serializedItemId
            serialNumber
            gtin
          }
          quarantinedQuantity
          pickedQuantity
          drift
        }
      }
      containers {
        serializedItemId
        serialNumber
        gtin
        type
        unitIds
      }
    }
  }
`;

export const ZONE_COUNT_UNIT_LOOKUP = gql`
  query zoneCountUnitLookup($companyId: ID!, $zoneId: ID!, $unit: ZoneCountUnitInput!) {
    zoneCountUnitLookup(companyId: $companyId, zoneId: $zoneId, unit: $unit) {
      serialNumber
      gtin
      status
      message
      canQuarantine
      canMoveHere
      product {
        ${PRODUCT_INFO_FIELDS}
      }
      lot
      expiration
      recordedZone {
        ${ZONE_FIELDS}
      }
      salesOrderNumber
    }
  }
`;

export const ZONE_COUNT_PRODUCT_LOOKUP = gql`
  query zoneCountProductLookup($companyId: ID!, $code: String, $gtin: String) {
    zoneCountProductLookup(companyId: $companyId, code: $code, gtin: $gtin) {
      ${PRODUCT_INFO_FIELDS}
    }
  }
`;

export const ZONE_COUNT_APPLY = gql`
  mutation zoneCountApply(
    $companyId: ID!
    $zoneId: ID!
    $quarantineZoneId: ID
    $missingUnitIds: [ID!]
    $foundUnits: [ZoneCountFoundUnitInput!]
    $shortages: [ZoneCountShortageInput!]
    $overages: [ZoneCountOverageInput!]
    $note: String
  ) {
    zoneCountApply(
      companyId: $companyId
      zoneId: $zoneId
      quarantineZoneId: $quarantineZoneId
      missingUnitIds: $missingUnitIds
      foundUnits: $foundUnits
      shortages: $shortages
      overages: $overages
      note: $note
    ) {
      zone {
        ${ZONE_FIELDS}
      }
      quarantineZone {
        ${ZONE_FIELDS}
      }
      missingQuantity
      foundQuantity
      quarantinedQuantity
      quarantinedUnits
      movedHereUnits
      packagingCount
      eventsWritten
    }
  }
`;

export type ZoneCountApplyResult = {
  zone: { id: string; name: string };
  quarantineZone?: { id: string; name: string } | null;
  missingQuantity: number;
  foundQuantity: number;
  quarantinedQuantity: number;
  quarantinedUnits: number;
  movedHereUnits: number;
  packagingCount: number;
  eventsWritten: number;
};

export type ZoneCountApplyVariables = {
  quarantineZoneId?: string | null;
  missingUnitIds: string[];
  foundUnits: Record<string, unknown>[];
  shortages: { zoneInventoryBalanceId: string; quantity: number }[];
  overages: Record<string, unknown>[];
  note?: string | null;
};

/**
 * Zone Count's data: the sheet is read on demand (open a zone, refresh, recount) rather than kept
 * live — it is what the operator is counting against, and must not reshuffle under them — and the
 * zone is kept in the URL (`?zoneId=`), so a reload, or a zone label scanned on the home screen,
 * opens straight onto it.
 */
function ZoneCountData() {
  const companyId = useRootStore((state) => state.selectedCompany?.id) as string;
  const client = useApolloClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const zoneId = searchParams.get('zoneId');
  const [sheet, setSheet] = useState<ZoneCountSheet | null>(null);
  const [sheetError, setSheetError] = useState<unknown>(null);
  const [loadingSheet, setLoadingSheet] = useState(false);

  const setZoneId = useCallback(
    (next: string | null) => {
      setSearchParams(next ? { zoneId: next } : {}, { replace: true });
    },
    [setSearchParams]
  );

  // Newest-request-wins: a slow read for a zone the operator has since left can never replace the
  // sheet on screen with another zone's records.
  const requestSeqRef = useRef(0);

  const loadSheet = useCallback(async (): Promise<ZoneCountSheet | null> => {
    const seq = ++requestSeqRef.current;
    setSheetError(null);
    if (!zoneId || !companyId) {
      setSheet(null);
      setLoadingSheet(false);
      return null;
    }
    setLoadingSheet(true);
    try {
      const { data } = await client.query({
        query: ZONE_COUNT_SHEET,
        variables: { companyId, zoneId },
        fetchPolicy: 'network-only'
      });
      if (seq !== requestSeqRef.current) return null;
      const next = (data?.zoneCountSheet as ZoneCountSheet) || null;
      setSheet(next);
      return next;
    } catch (e) {
      if (seq === requestSeqRef.current) {
        setSheet(null);
        setSheetError(e);
      }
      return null;
    } finally {
      if (seq === requestSeqRef.current) setLoadingSheet(false);
    }
  }, [client, companyId, zoneId]);

  useEffect(() => {
    // A different zone starts from no sheet: the previous zone's records are never counted against.
    setSheet(null);
    loadSheet();
  }, [loadSheet]);

  const lookupUnit = useCallback(
    async (unit: Record<string, unknown>): Promise<ZoneCountUnitLookup> => {
      const { data } = await client.query({
        query: ZONE_COUNT_UNIT_LOOKUP,
        variables: { companyId, zoneId, unit },
        fetchPolicy: 'network-only'
      });
      return data?.zoneCountUnitLookup;
    },
    [client, companyId, zoneId]
  );

  const lookupProduct = useCallback(
    async (code: string | null, gtin: string | null): Promise<ZoneCountProduct | null> => {
      const { data } = await client.query({
        query: ZONE_COUNT_PRODUCT_LOOKUP,
        variables: { companyId, code, gtin },
        fetchPolicy: 'network-only'
      });
      return data?.zoneCountProductLookup || null;
    },
    [client, companyId]
  );

  const apply = useCallback(
    async (variables: ZoneCountApplyVariables): Promise<ZoneCountApplyResult> => {
      const { data } = await client.mutate({
        mutation: ZONE_COUNT_APPLY,
        variables: { companyId, zoneId, ...variables }
      });
      return data?.zoneCountApply;
    },
    [client, companyId, zoneId]
  );

  return (
    <ZoneCount
      companyId={companyId}
      zoneId={zoneId}
      setZoneId={setZoneId}
      sheet={sheet && sheet.zone?.id === zoneId ? sheet : null}
      loadingSheet={loadingSheet}
      sheetError={sheetError}
      onReload={loadSheet}
      lookupUnit={lookupUnit}
      lookupProduct={lookupProduct}
      onApply={apply}
    />
  );
}

export default ZoneCountData;
